import axios from 'axios';
import { onMounted } from 'vue';
import { defaultStagRaw } from '~/config/constants.config';
import { useStoreStag } from '~/stores/client/stag';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { softswiss } from '~/stores/transport/softswiss';
import { getCookieValue } from '~/utils/cookie';

export const useClickTracker = () => {
  const apiUrl = '/backend/track';

  const getCleanUrlStag = (): string | null => {
    const params = new URLSearchParams(window.location.search);
    const stag = params.get('stag');
    return stag ? stag.split('_')[0] : null;
  };

  const checkIfShouldForceStag = () => {
    const forcedOGStags = [defaultStagRaw, '161295', '159170'];

    let ogStagRaw = '';
    let lastClickRaw = '';

    const ogStagObject = localStorage.getItem('og_stag_clicked');

    if (ogStagObject) {
      ogStagRaw = JSON.parse(ogStagObject || '{}')?.stag || '';
    }

    const lastClickObject = localStorage.getItem('last_stag_click');

    if (lastClickObject) {
      lastClickRaw = JSON.parse(lastClickObject || '{}')?.stag || '';
    }

    const lastSavedStag =
      useStoreStag().lastSavedStag.split('_')[0] || localStorage.getItem('stag')?.split('_')[0];
    if (
      forcedOGStags.some((el) => el.includes(ogStagRaw)) &&
      lastClickRaw !== ogStagRaw &&
      (!lastSavedStag || !forcedOGStags.some((el) => el.includes(lastSavedStag)))
    ) {
      return true;
    }

    return false;
  };

  const generateAndStoreUniqId = () => {
    let id = localStorage.getItem('uniq_id');

    if (!id) {
      try {
        id = crypto.randomUUID();
        localStorage.setItem('uniq_id', id);
        localStorage.setItem('visit_count', '0');
      } catch (error) {
        console.warn('LocalStorage blocked', error);
        id = crypto.randomUUID();
      }
    }

    useStoreStag().setUniqId(id);
    return id;
  };

  const getPlayerStats = async () => {
    try {
      const response = await softswiss().get({
        path: '/api/player/stats',
        force: true,
      });

      if (response?.['ga clientId']) {
        return response['ga clientId'];
      }
    } catch (error) {
      console.error('Error player stats:', error);
    }
  };

  const canTrackStag = (stag: string): boolean => {
    const lastClickRaw = localStorage.getItem('last_stag_click');
    if (!lastClickRaw) {
      return true;
    }
    try {
      const lastClick = JSON.parse(lastClickRaw);
      const now = Date.now();
      const weekMs = 7 * 24 * 60 * 60 * 1000;
      return lastClick.stag !== stag || now - new Date(lastClick.timestamp).getTime() > weekMs;
    } catch {
      return true;
    }
  };

  const markStagTracked = (stag: string, visitType: string, timestamp: string) => {
    localStorage.setItem('last_stag_click', JSON.stringify({ stag, visitType, timestamp }));
    if (!localStorage.getItem('og_stag_clicked')) {
      localStorage.setItem('og_stag_clicked', JSON.stringify({ stag, visitType, timestamp }));
    }
  };

  const trackClick = async (last_rm_visit_stag: string, promo_code?: string) => {
    if (process.server) {
      return;
    }

    if (!localStorage.getItem('uniq_id')) {
      generateAndStoreUniqId();
    }

    const nowDataTime = new Date().toISOString();
    const realVisitStag = getCleanUrlStag();
    const btag = getCookieValue('btag');
    const btagReserv = getCookieValue('SourceStagFact');
    const stagToUse =
      last_rm_visit_stag || localStorage.getItem('stag') || btag || btagReserv || '';

    // tracking is promo_code
    const shouldTrackStagToUse = stagToUse && (canTrackStag(stagToUse) || promo_code);
    const shouldTrackRealVisitStag = realVisitStag && (canTrackStag(realVisitStag) || promo_code);

    const navEntries = performance.getEntriesByType('navigation');
    const navType = navEntries.length
      ? (navEntries[0] as PerformanceNavigationTiming).type
      : performance.navigation?.type === 1
      ? 'reload'
      : 'navigate';
    let visitType: string;
    if (navType === 'reload') {
      visitType = 'refresh';
    } else {
      visitType = 'other';
    }

    if (realVisitStag && !localStorage.getItem('og_stag_clicked')) {
      markStagTracked(realVisitStag, visitType, nowDataTime);
    }

    // Allow tracking for authorized users if promo_code is provided
    if (
      (!shouldTrackStagToUse && !shouldTrackRealVisitStag) ||
      (useStoreSsPlayer().isPlayerAuthorised && !promo_code)
    ) {
      if (realVisitStag) {
        localStorage.setItem('last_url_stag', realVisitStag);
      }
      return;
    }

    const trackedStag = shouldTrackStagToUse ? stagToUse : realVisitStag;

    localStorage.setItem('last_url_stag', realVisitStag || '');
    localStorage.setItem('last_visit_type', visitType);

    markStagTracked(trackedStag || '', visitType, nowDataTime);

    const ogStagRaw = localStorage.getItem('og_stag_clicked');
    let original_stag = '';
    let original_visit_time = null;
    let original_visit_type = '';
    if (ogStagRaw) {
      try {
        const parsed = JSON.parse(ogStagRaw);
        if (realVisitStag === parsed.stag) {
          original_stag = realVisitStag || '';
        } else {
          original_stag = parsed.stag;
        }
        original_visit_time = parsed.timestamp || null;
        original_visit_type = parsed.visitType || '';
      } catch {}
    }
    // Only consider visitType change for specific search engine hosts
    const visitTypeChangedFromSearchEngine = (() => {
      if (original_visit_type !== 'search_engine' || !document.referrer) {
        return false;
      }
      const refHost = new URL(document.referrer).host;
      const searchEngines = ['google.', 'bing.com', 'yahoo.', 'duckduckgo.com'];
      return searchEngines.some((engine) => refHost.includes(engine));
    })();

    const rawStagChanged =
      stagToUse !== original_stag ||
      visitTypeChangedFromSearchEngine ||
      (!stagToUse && !!original_stag);

    const currentUrl = window.location.href || '';
    const is_stag_changed =
      currentUrl.includes('customer.io') || currentUrl.includes('customer')
        ? false
        : rawStagChanged;

    let visitCount = 0;
    try {
      visitCount = Number(localStorage.getItem('visit_count') || '0');
      visitCount += 1;
      localStorage.setItem('visit_count', visitCount.toString());
    } catch (error) {
      console.warn('localStorage blocked for visit_count, using fallback', error);
      try {
        const sessionCount = Number(sessionStorage.getItem('visit_count') || '0');
        visitCount = sessionCount + 1;
        sessionStorage.setItem('visit_count', visitCount.toString());
      } catch (sessionError) {
        console.error('localStorage blocked for visit_count', sessionError);
      }
    }

    const doubleCleanStag = (stag: string | null): string | null => {
      if (!stag) {
        return defaultStagRaw;
      }
      const cleaned = stag.split('_')[0];
      return /^\d+$/.test(cleaned) ? cleaned : defaultStagRaw;
    };

    // Get ss_ga_id
    const ga_id = await getPlayerStats();
    if (ga_id) {
      localStorage.setItem('ga_id', ga_id);
    }

    const body = {
      real_visit_stag: doubleCleanStag(realVisitStag || defaultStagRaw),
      // last_rm_visit_stag: stagToUse, // old_name_current_stag || click_stag тепер це last_rm_visit_stag
      original_stag: doubleCleanStag(original_stag),
      is_stag_changed,
      timestamp: nowDataTime, // new Date().toISOString(), // name click_time тепер це visit_time //real_visit_time
      original_timestamp: original_visit_time, // original_click_time тепер це original_visit_time
      uniq_id: useStoreStag().uniq_id, // utm_term
      visit_number: visitCount,
      visit_url: window.location.href,
      domain: window.location.host,
      visit_type: visitType,
      referrer: document.referrer.replace(/^https?:\/\//, '') || '',
      promo_code: (promo_code || '').toUpperCase(),
      ss_ga_id: ga_id,
    };

    try {
      await axios.post(apiUrl, body);
      if (trackedStag === realVisitStag) {
        localStorage.setItem('last_rm_visit_stag', realVisitStag || '');
      } else if (!realVisitStag) {
        localStorage.removeItem('last_rm_visit_stag');
      }
    } catch (err) {
      console.error('Click err:', err);
      console.error('Failed body data:', body);
    }
  };

  onMounted(() => {
    if (!process.server) {
      generateAndStoreUniqId();
    }
  });

  return {
    trackClick,
    generateAndStoreUniqId,
    checkIfShouldForceStag,
  };
};
